import { type A2uiMessage, A2uiMessageListSchema } from '@a2ui/web_core/v0_9';
import { z } from 'zod';

import {
  CHART_CATALOG_ID,
  CHART_COMPONENT_NAME,
  CHART_ROOT_ID,
  CHART_SURFACE_ID,
} from './catalogConstants';

const chartNodeSchema = z
  .object({
    id: z.literal(CHART_ROOT_ID),
    component: z.literal(CHART_COMPONENT_NAME),
  })
  .passthrough();

const createSurfaceMessageSchema = z
  .object({
    version: z.string(),
    createSurface: z
      .object({
        surfaceId: z.literal(CHART_SURFACE_ID),
        catalogId: z.literal(CHART_CATALOG_ID),
      })
      .passthrough(),
  })
  .strict();

const updateComponentsMessageSchema = z
  .object({
    version: z.string(),
    updateComponents: z
      .object({
        surfaceId: z.literal(CHART_SURFACE_ID),
        components: z.tuple([chartNodeSchema]),
      })
      .passthrough(),
  })
  .strict();

const chartTreeShapeSchema = z.tuple([
  createSurfaceMessageSchema,
  updateComponentsMessageSchema,
]);

export type ChartTreeValidation =
  | {
      ok: true;
      messages: A2uiMessage[];
      errors: [];
    }
  | { ok: false; errors: string[] };

const formatIssues = (error: z.ZodError): string[] =>
  error.issues.map(
    (issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`
  );

export const validateChartTree = (raw: unknown): ChartTreeValidation => {
  const structural = A2uiMessageListSchema.safeParse(raw);
  if (!structural.success) {
    return { ok: false, errors: formatIssues(structural.error) };
  }

  const shape = chartTreeShapeSchema.safeParse(structural.data);
  if (!shape.success) {
    return { ok: false, errors: formatIssues(shape.error) };
  }

  // Hand back what was actually validated, not what arrived.
  return { ok: true, messages: structural.data, errors: [] };
};

/** The chart node's title, for the artifact header. */
const titleSchema = z.tuple([
  z.unknown(),
  z.object({
    updateComponents: z.object({
      components: z.tuple([z.object({ title: z.string().min(1) })]),
    }),
  }),
]);

/** Title for the header, or undefined to fall back. Presentation only — never a reason to refuse to render. */
export const readChartTitle = (raw: unknown): string | undefined => {
  const parsed = titleSchema.safeParse(raw);

  return parsed.success
    ? parsed.data[1].updateComponents.components[0].title
    : undefined;
};
